// src/components/SubscriptionManager.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    Divider,
    Card,
    CardContent,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import ErrorModal from './ErrorModal';
import { useAuth } from '@/contexts/AuthProvider';
import { useDispatch } from 'react-redux';
import {
    setTemporaryPremium,
    clearTemporaryPremium
} from '@/store/premiumSlice';
import SubscriptionButton from './SubscriptionButton';
import StripeSubscriptionButton from './StripeSubscriptionButton';
import { useFirestore } from '@/hooks/useFirestore';
import { Capacitor } from '@capacitor/core';
import { authedPost } from '@/services/apiClient';
import { colors, radius } from '@/theme/tokens';
import SubscriptionStatusCard, { SubscriptionSnapshot } from './SubscriptionStatusCard';
import { PREMIUM_CONFIG } from '@/components/PremiumUpgradeModal';
import { PASS_2JI, isPassOnSale } from '@/config/pass2ji';

const PRODUCT_NAME = '診断士RoadMap Premium';

// Firestore Timestamp / Date / ISO文字列 のどれで来ても Date にする
const toDate = (v: any): Date | null => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};

const FN_BASE = 'https://us-central1-chusho-app.cloudfunctions.net/api';

const FullScreenLoadingOverlay: React.FC = () => (
    <Box
        sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300
        }}
    >
        <CircularProgress color="inherit" />
    </Box>
);

interface Props {
    dense?: boolean; // AccountPage では true に
}

// Stripe Price IDs（PayPal の Plan ID は 2026-09-29 に経路ごと削除）
const STRIPE_PRICE_ID_LIVE = 'price_1SQrQOJm6nYxRNS1wU8ldhBN'; // ¥500/month
const STRIPE_PRICE_ID_TEST = 'price_1SQ7aOJm6nYxRNS1bNPnl6Sv'; // ¥500/month (test)

// プラン比較の実体。**実装で確認できた差分だけを書く**（2026-09-24 監査）。
// 無料でも使えるものを「×」と書かない／実装の無いものを「✓」と書かない。
const PLAN_ROWS: Array<{ feature: string; note?: string; free: true | false | string; premium: true | string }> = [
    { feature: 'AI問題（弱点に合わせた自動作問）', free: '3問まで', premium: '無制限' },
    // 回数は定数から出す（2026-10-04）。ここの100は PREMIUM_CONFIG から写し間違えた値で、
    // サーバ（aiHandler の DAILY_CAP_PREMIUM）は 200 だった。
    { feature: 'AIチャットで質問', free: `1問につき${PREMIUM_CONFIG.FREE_AI_CHAT_PER_QUESTION}回`, premium: `1日${PREMIUM_CONFIG.PREMIUM_DAILY_AI_CHAT_LIMIT}回まで` },
    { feature: '二次試験のAI添削', free: `1日${PREMIUM_CONFIG.FREE_DAILY_GRADE_LIMIT}回まで`, premium: `1日${PREMIUM_CONFIG.PREMIUM_DAILY_GRADE_LIMIT}回まで` },
    { feature: '体感型解説の演習・応用セクション', note: '要点は無料で読めます', free: '冒頭のみ', premium: 'すべて' },
    { feature: '過去問演習と解説', free: true, premium: true },
    { feature: '論点解説202本・ミニ模試', free: true, premium: true },
    { feature: '学習履歴・ブックマーク・コミュニティ', free: true, premium: true },
];

const SubscriptionManager: React.FC<Props> = ({ dense = false }) => {
    const { user } = useAuth();
  const firestore = useFirestore();
    const dispatch = useDispatch();

    const [membership, setMembership] = useState<'premium' | 'regular'>('regular');
    const [paymentProvider, setPaymentProvider] = useState<string>('');
    const [subscriptionID, setSubscriptionID] = useState('');
    const [stripeSubscriptionId, setStripeSubscriptionId] = useState<string>('');
    const [isTestMode, setIsTestMode] = useState<boolean>(false); // サブスクリプションがテストモードかどうか
    const [snapshot, setSnapshot] = useState<SubscriptionSnapshot>({});
    const [processing, setProcessing] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [useSandbox, setUseSandbox] = useState(() => {
        // localStorageから初期値を読み込む
        const saved = localStorage.getItem('subscription_useSandbox');
        return saved === 'true';
    });
    const [paymentClickCount, setPaymentClickCount] = useState(0);
    const [showSandboxOption, setShowSandboxOption] = useState(() => {
        // localStorageから初期値を読み込む
        const saved = localStorage.getItem('subscription_showSandboxOption');
        return saved === 'true';
    });

    /* ----- useSandboxの状態をlocalStorageに保存 ----- */
    useEffect(() => {
        localStorage.setItem('subscription_useSandbox', String(useSandbox));
    }, [useSandbox]);

    /* ----- showSandboxOptionの状態をlocalStorageに保存 ----- */
    useEffect(() => {
        localStorage.setItem('subscription_showSandboxOption', String(showSandboxOption));
    }, [showSandboxOption]);

    /* ----- Firestore リッスン ----- */
    useEffect(() => {
        if (!user?.uid) return;
        const unsub = onSnapshot(firestore.getUserDoc(user.uid), (snap) => {
            if (!snap.exists()) return;
            const d = snap.data();
            const m = d.membership === 'premium' ? 'premium' : 'regular';
            setMembership(m);
            setPaymentProvider(d.paymentProvider || '');
            setSubscriptionID(d.subscriptionID || '');
            setStripeSubscriptionId(d.stripeSubscriptionId || '');
            setIsTestMode(d.isTestMode || false); // テスト/本番モードを取得
            // マイページ表示用スナップショット（Stripe webhook が Firestore に写した値。クライアントは Stripe を叩かない）
            setSnapshot({
                paymentProvider: d.paymentProvider || '',
                stripeSubscriptionId: d.stripeSubscriptionId || null,
                subscriptionStatus: d.subscriptionStatus || (m === 'premium' ? 'active' : undefined),
                cancelAtPeriodEnd: !!d.cancelAtPeriodEnd,
                cancelAt: toDate(d.cancelAt),
                premiumUntil: toDate(d.premiumUntil),
                priceNickname: d.priceNickname || null,
                priceAmount: d.priceAmount ?? null,
                priceCurrency: d.priceCurrency || null,
                cardLast4: d.cardLast4 || null,
                isTestMode: !!d.isTestMode,
            });
            if (m === 'premium') {
                setProcessing(false);
                dispatch(clearTemporaryPremium());
            }
        });
        return () => unsub();
    }, [user, dispatch]);

    const handlePaymentMethodClick = () => {
        const newCount = paymentClickCount + 1;
        setPaymentClickCount(newCount);
        if (newCount >= 3) {
            setShowSandboxOption(true);
        }
    };

    const hasStripeSubscription = paymentProvider === 'stripe' && stripeSubscriptionId;

    // 2026-09-29: PayPal の解約導線を削除した。サーバー側の /paypal/* を
    // （署名検証が無く誰でも premium を付けられる穴があったため）経路ごと落としたので、
    // 押しても 404 になるボタンを残さない。PayPal の記録が残っている利用者は
    // **課金されていない sandbox の記録**なので、解約すべき支払いは存在しない。

    /* ----- 「購読を管理」: サーバーで Customer Portal セッションを発行して遷移（解約は期間終了時・支払い方法変更・請求履歴） ----- */
    const handleManageStripe = async () => {
        const res = await authedPost('/stripe/create-portal-session', {
            userId: user?.uid,
            returnUrl: window.location.href,
        });
        if (!res.ok) throw new Error(await res.text());
        const { url } = (await res.json()) as { url: string };
        window.location.assign(url);
    };

    /* ----- 期限などが未取得の既存会員向け: サーバー経由で Stripe から写し直す（onSnapshot で反映） ----- */
    const handleSyncStripe = async () => {
        try {
            await authedPost('/stripe/sync-subscription', { userId: user?.uid });
        } catch (e) {
            console.warn('subscription sync failed', e);
        }
    };

    return (
        <Box sx={{ mt: dense ? 4 : 0 }}>
            {/* プラン比較表 */}
            <Typography
                variant="h6"
                gutterBottom
                sx={{ textAlign: 'center', mb: 3 }}
            >
                プラン比較
            </Typography>

            {/* プラン比較。
                **ここに書いてよいのは、実装で確かめた差分だけ。**
                2026-09-24 監査: 以前は「オフライン学習コンテンツ」「専用サポートチャネル」など
                実装の無い特典を並べ、逆に無料でも使える機能(過去問解説・学習プラン・進捗分析)を
                「無料=×」と表示していた。払っても得られないものを約束していた状態だったので、
                実際に Premium で変わる4点だけに直した。数値の出典:
                  - AI問題 / AIチャット: PREMIUM_CONFIG (PremiumUpgradeModal.tsx)
                  - 2次AI添削: GRADE_CAP_FREE=10 / GRADE_CAP_PREMIUM=100 (functions/handlers/gradingHandler.ts)
                  - 体感型解説: components/learn/PremiumGate.tsx */}
            <TableContainer
                component={Paper}
                sx={{
                    mb: 4,
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.borderNeutral}`,
                    borderRadius: `${radius.lg}px`,
                    backgroundImage: 'none',
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: colors.surfaceHigh }}>
                            <TableCell sx={{ fontWeight: 700, width: '46%', borderColor: colors.borderNeutral }}>
                                できること
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, width: '27%', borderColor: colors.borderNeutral }}>
                                無料
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 700, width: '27%', color: colors.gold, borderColor: colors.borderNeutral }}
                            >
                                プレミアム
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {PLAN_ROWS.map((row) => (
                            <TableRow key={row.feature}>
                                <TableCell sx={{ borderColor: colors.borderNeutral }}>
                                    {row.feature}
                                    {row.note && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                            {row.note}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center" sx={{ borderColor: colors.borderNeutral }}>
                                    {row.free === true ? (
                                        <CheckCircleIcon sx={{ color: colors.success, fontSize: 20 }} />
                                    ) : row.free === false ? (
                                        <CancelIcon sx={{ color: colors.textDisabled, fontSize: 20 }} />
                                    ) : (
                                        <Typography variant="body2">{row.free}</Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center" sx={{ borderColor: colors.borderNeutral }}>
                                    {row.premium === true ? (
                                        <CheckCircleIcon sx={{ color: colors.success, fontSize: 20 }} />
                                    ) : (
                                        <Typography variant="body2" sx={{ color: colors.gold, fontWeight: 700 }}>
                                            {row.premium}
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: colors.surfaceHigh }}>
                            <TableCell sx={{ fontWeight: 700, borderColor: colors.borderNeutral }}>月額</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, borderColor: colors.borderNeutral }}>
                                無料
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 700, color: colors.gold, borderColor: colors.borderNeutral }}
                            >
                                ¥500
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Divider sx={{ my: 4 }} />

            {/* サブスクリプション管理 */}
            <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                {membership === 'premium' ? 'プレミアムアカウント' : 'サブスクリプション登録'}
            </Typography>

            {membership === 'premium' ? (
                <Box sx={{ textAlign: 'center' }}>
                    {hasStripeSubscription ? (
                        /* Stripe: プラン名・状態・期限・残り日数・購読を管理(ポータル)・返金相談 */
                        <Box sx={{ textAlign: 'left' }}>
                            <SubscriptionStatusCard
                                snapshot={snapshot}
                                productName={PRODUCT_NAME}
                                onManage={handleManageStripe}
                                onSync={handleSyncStripe}
                                /* ネイティブでは外部の決済管理画面へ誘導しない(ガイドライン 3.1.1) */
                                manageDisabled={Capacitor.isNativePlatform()}
                                manageHint="購読の変更・解約・請求履歴は Web 版（chusho-app.web.app）のアカウントページから行えます。"
                            />
                        </Box>
                    ) : (
                        <Paper sx={{ p: 3, bgcolor: '#e3f2fd', mb: 2 }}>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                現在のプラン: <strong>Premium</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                支払い方法: 不明
                            </Typography>
                        </Paper>
                    )}
                    {hasStripeSubscription ? null : Capacitor.isNativePlatform() ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            サブスクリプションは {Capacitor.getPlatform() === 'ios' ? 'App Store' : 'Google Play'} の
                            「サブスクリプション」設定から管理・キャンセルできます。
                        </Typography>
                    ) : null}
                </Box>
            ) : Capacitor.isNativePlatform() ? (
                /* ネイティブ (iOS/Android): App Store / Google Play の内課金のみ表示
                   (外部決済 UI はガイドライン 3.1.1 のため表示しない) */
                <Box sx={{ textAlign: 'center' }}>
                    <SubscriptionButton nativePackageId="$rc_monthly" />
                </Box>
            ) : (
                <Box>
                    {/* 2次直前パス（期間限定）への導線。販売期間が終わったら自動で消える。
                        値段と期限は src/config/pass2ji.ts の1か所から読む（画面ごとに書くとズレる） */}
                    {isPassOnSale() && (
                        <Paper
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: colors.surfaceHigh,
                                border: `1px solid ${colors.gold}`,
                                borderRadius: `${radius.lg}px`,
                                backgroundImage: 'none',
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {PASS_2JI.name}（{PASS_2JI.amountLabel}・{PASS_2JI.endsAtLabel}まで）
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
                                二次試験までの期間限定。お支払いは1回のみで、自動更新はありません。
                            </Typography>
                            <Button variant="outlined" size="small" href={PASS_2JI.path}>
                                内容を見る
                            </Button>
                        </Paper>
                    )}

                    {/* 支払い方法選択 */}
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <Typography
                            variant="body1"
                            sx={{ mb: 2, cursor: 'pointer', userSelect: 'none' }}
                            onClick={handlePaymentMethodClick}
                        >
                            支払い方法を選択してください
                        </Typography>

                        {/* サンドボックスモード切り替え（3回クリック後に表示） */}
                        {showSandboxOption && (
                            <Box sx={{ mb: 3 }}>
                                <Paper sx={{ p: 2, bgcolor: '#fff3e0', display: 'inline-block' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={useSandbox}
                                                onChange={(e) => setUseSandbox(e.target.checked)}
                                                color="warning"
                                            />
                                        }
                                        label="🧪 テストモード（サンドボックス）を使用"
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        開発者向け: 実際の支払いは発生しません
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                        {/* 【2026-09-27 削除】Stripe / PayPal の選択トグル。
                            本番に出ていた PayPal は **sandbox の client ID** で動いており、
                            実利用者が選んでも決済を完了できない壊れた導線だった
                            （本番バンドルに Live の client ID は入っていなかった＝実測）。
                            live 化には PayPal ビジネス口座と CEO 操作が要るため、
                            **課金は Stripe live(¥500/月) の1本にする**（司令塔 2026-09-27 判断）。
                            **2026-09-29 追記**: サーバー側の /paypal/* も経路ごと削除したので、
                            残していた解約導線もここで落とした。 */}
                    </Box>

                    {/* 選択された支払い方法のボタン */}
                    <Box sx={{ textAlign: 'center' }}>
                        <StripeSubscriptionButton
                            priceId={useSandbox ? STRIPE_PRICE_ID_TEST : STRIPE_PRICE_ID_LIVE}
                            existingSubscriptionID={hasStripeSubscription ? stripeSubscriptionId : ''}
                            useSandbox={useSandbox}
                        />
                        {/* 購入画面から1クリックで到達できるようにする（特商法・司令塔 2026-09-28） */}
                        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
                            <Link href="/tokushoho" target="_blank" rel="noopener noreferrer" underline="hover">
                                特定商取引法に基づく表記
                            </Link>
                            {' ・ '}
                            <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" underline="hover">
                                プライバシーポリシー
                            </Link>
                        </Typography>
                    </Box>
                </Box>
            )}

            {processing && <FullScreenLoadingOverlay />}
            {errorMsg && (
                <ErrorModal open message={errorMsg} onClose={() => setErrorMsg('')} />
            )}
        </Box>
    );
};

export default SubscriptionManager;
